import { useTranslation } from 'react-i18next';
import { Bell, BellOff, BellRing } from 'lucide-react';
import { Alert, Button } from './UI';
import { usePushNotifications } from '../hooks/usePushNotifications';

/** Kontoeinstellung: Web Push auf diesem Gerät ein- und ausschalten. */
export const PushNotificationCard: React.FC = () => {
  const { t } = useTranslation();
  const { status, busy, error, enable, disable } = usePushNotifications();

  if (status === 'loading') return null;

  const infoKey = status === 'unsupported' || status === 'disabled' || status === 'denied' ? status : null;

  return (
    <section className="card">
      <h2 className="mb-1 flex items-center gap-2 text-lg font-bold text-dark-50">
        {status === 'on' ? <BellRing size={20} className="text-green-400" /> : <Bell size={20} />}
        {t('account:push.title')}
      </h2>
      <p className="mb-4 text-sm text-dark-400">{t('account:push.description')}</p>

      {error && (
        <div className="mb-3">
          <Alert type="error" message={t('account:push.error')} />
        </div>
      )}

      {infoKey ? (
        <p className="flex items-start gap-2 text-sm text-dark-300">
          <BellOff size={16} className="mt-0.5 shrink-0 text-dark-400" />
          {t(`account:push.${infoKey}`)}
        </p>
      ) : status === 'on' ? (
        <div className="space-y-3">
          <p className="text-sm text-green-400">{t('account:push.enabled')}</p>
          <Button type="button" variant="ghost" loading={busy} onClick={disable}>
            {t('account:push.disable')}
          </Button>
        </div>
      ) : (
        <Button type="button" loading={busy} onClick={enable} icon={<BellRing size={16} />}>
          {t('account:push.enable')}
        </Button>
      )}
    </section>
  );
};

/** Kompakter Hinweis im Benachrichtigungs-Fenster - nur sichtbar, solange Push auf diesem Gerät noch aus ist. */
export const PushEnablePrompt: React.FC = () => {
  const { t } = useTranslation();
  const { status, busy, enable } = usePushNotifications();

  if (status !== 'off') return null;

  return (
    <button
      type="button"
      onClick={enable}
      disabled={busy}
      className="mb-2 flex w-full items-center gap-3 rounded-xl border border-primary-500/30 bg-primary-500/10 px-3 py-2.5 text-left text-sm text-primary-200 transition-colors hover:bg-primary-500/15 disabled:opacity-60"
    >
      <BellRing size={18} className="shrink-0" />
      <span className="min-w-0 flex-1">{t('nav:notifications.pushPrompt')}</span>
    </button>
  );
};
